import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Intake from "@/pages/Intake";
import IntakeWizard from "@/pages/IntakeWizard";
import IntakeRequests from "@/pages/IntakeRequests";
import Approvals from "@/pages/Approvals";
import { LEGACY_REDIRECTS } from "@/lib/legacyRedirects";
import Backlog from "@/pages/Backlog";
import SprintBoard from "@/pages/SprintBoard";
import ItemDetail from "@/pages/ItemDetail";
import Prioritize from "@/pages/Prioritize";
import ProductDetail from "@/pages/ProductDetail";
import Roadmap from "@/pages/Roadmap";
import ADOSettings from "@/pages/ADOSettings";
import AgentSettings from "@/pages/AgentSettings";
import AgentSkills from "@/pages/AgentSkills";
import Reports from "@/pages/Reports";
import Metrics from "@/pages/Metrics";
import UserManagement from "@/pages/UserManagement";
import Observability from "@/pages/Observability";
import NotificationSettings from "@/pages/NotificationSettings";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]"><span className="w-8 h-8 border-3 border-[#2d9cdb] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Scoped roles see a narrow app with a single landing page. They are blocked
// from internal pages and sent to their landing page instead. The backend
// enforces the same limits; these guards only shape the UI.
const SCOPED_ROLE_HOME = {
  requester: '/intake-requests?tab=mine',
  intake_reviewer: '/intake-requests',
};

// Internal pages: everyone except scoped roles.
function InternalOnly({ children }) {
  const { user } = useAuth();
  const home = SCOPED_ROLE_HOME[user?.role];
  if (home) return <Navigate to={home} replace />;
  return children;
}

// Old intake URLs redirect to the register (lib/legacyRedirects.js).
function LegacyRedirect({ to }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

// Dashboard is not for scoped roles; route "/" based on role.
function HomeRoute() {
  const { user } = useAuth();
  const home = SCOPED_ROLE_HOME[user?.role];
  if (home) return <Navigate to={home} replace />;
  return <Dashboard />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/intake" element={<InternalOnly><Intake /></InternalOnly>} />
            <Route path="/intake/use-case" element={<IntakeWizard />} />
            {/* The intake register, for every role; the backend scopes what each sees. */}
            <Route path="/intake-requests" element={<IntakeRequests />} />
            <Route path="/intake-requests/:itemId" element={<IntakeRequests />} />
            <Route path="/approvals" element={<InternalOnly><Approvals /></InternalOnly>} />
            {/* Replaced by the intake register (2026-10-21). The old page files stay
                one release for rollback and are deleted after it. */}
            {LEGACY_REDIRECTS.map(({ path, to, internal }) => (
              <Route
                key={path}
                path={path}
                element={internal ? <InternalOnly><LegacyRedirect to={to} /></InternalOnly> : <LegacyRedirect to={to} />}
              />
            ))}
            <Route path="/backlog" element={<InternalOnly><Backlog /></InternalOnly>} />
            <Route path="/backlog/:itemId" element={<InternalOnly><ItemDetail /></InternalOnly>} />
            <Route path="/prioritize/:itemId" element={<InternalOnly><Prioritize /></InternalOnly>} />
            <Route path="/products/:productId" element={<InternalOnly><ProductDetail /></InternalOnly>} />
            <Route path="/sprint-board" element={<InternalOnly><SprintBoard /></InternalOnly>} />
            <Route path="/roadmap" element={<InternalOnly><Roadmap /></InternalOnly>} />
            <Route path="/settings/ado" element={<InternalOnly><ADOSettings /></InternalOnly>} />
            <Route path="/settings/agents" element={<InternalOnly><AgentSettings /></InternalOnly>} />
            <Route path="/settings/users" element={<InternalOnly><UserManagement /></InternalOnly>} />
            <Route path="/settings/notifications" element={<InternalOnly><NotificationSettings /></InternalOnly>} />
            <Route path="/settings/observability" element={<InternalOnly><Observability /></InternalOnly>} />
            <Route path="/reports" element={<InternalOnly><Reports /></InternalOnly>} />
            {/* Old standalone routes — preserve as redirects so bookmarks keep working. */}
            <Route path="/reports/templates" element={<Navigate to="/reports?tab=templates" replace />} />
            <Route path="/settings/agents/skills" element={<Navigate to="/settings/agents?tab=skills" replace />} />
            <Route path="/settings/agent-skills" element={<Navigate to="/settings/agents?tab=skills" replace />} />
            <Route path="/metrics" element={<InternalOnly><Metrics /></InternalOnly>} />
          </Route>
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] dark:bg-[#1a1a2e]">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-300 dark:text-gray-700">404</h1>
                <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">Page not found</p>
                <a href="/" className="mt-6 inline-block px-4 py-2 bg-[#2d9cdb] text-white rounded-lg hover:bg-[#2d9cdb]/90">
                  Go to Dashboard
                </a>
              </div>
            </div>
          } />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
